import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, RotateCcw, AlertTriangle } from "lucide-react";

/**
 * WebcamCapture — live <video> preview, JPEG capture, ~150KB target.
 *
 * Props:
 *   - prompt: string shown above the frame ("Look at the camera")
 *   - actionLabel: capture button label
 *   - onCapture(dataUrl): called when user confirms a capture
 *   - onCancel?(): optional cancel handler
 *   - testIdPrefix: used to build data-testid attributes
 *   - showRetake?: bool — show retake after first capture (default true)
 */
export default function WebcamCapture({
  prompt = "Look at the camera",
  actionLabel = "Capture",
  onCapture,
  onCancel,
  testIdPrefix = "webcam",
  showRetake = true,
  autoStart = true,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [preview, setPreview] = useState(null);
  const [capturing, setCapturing] = useState(false);

  // Bind/rebind the live stream whenever the <video> element appears.
  //
  // Why a callback ref: `setPreview(...)` toggles between <video> and <img>,
  // which unmounts the <video>. The next "Retake" remounts a fresh <video>
  // element that needs `srcObject` attached again. The one-time `useEffect`
  // below only fires on mount, so without this callback the new <video>
  // would render black.
  const attachVideo = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      try {
        node.srcObject = streamRef.current;
        node.play().catch(() => {});
        setReady(true);
      } catch (_) {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    if (!autoStart) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: 720, height: 720 },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        // If the <video> is already in the DOM, attach immediately; if it
        // remounts later (Retake) the callback ref takes care of rebinding.
        if (videoRef.current) {
          try {
            videoRef.current.srcObject = stream;
            await videoRef.current.play().catch(() => {});
            setReady(true);
          } catch (_) { /* ignore */ }
        }
      } catch (e) {
        setError(
          e?.name === "NotAllowedError"
            ? "Camera permission denied. Allow camera access to capture face."
            : "Could not access the camera on this device.",
        );
      }
    })();
    return () => {
      cancelled = true;
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [autoStart]);

  const capture = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    setCapturing(true);
    try {
      const v = videoRef.current;
      const c = canvasRef.current;
      const size = Math.min(v.videoWidth || 480, v.videoHeight || 480, 480);
      c.width = size;
      c.height = size;
      const ctx = c.getContext("2d");
      const sx = Math.max(0, ((v.videoWidth || size) - size) / 2);
      const sy = Math.max(0, ((v.videoHeight || size) - size) / 2);
      ctx.drawImage(v, sx, sy, size, size, 0, 0, size, size);
      // Compress to ~150KB. Walk quality down from 0.85.
      let dataUrl = c.toDataURL("image/jpeg", 0.85);
      let q = 0.85;
      while (dataUrl.length > 220_000 && q > 0.4) {
        q -= 0.1;
        dataUrl = c.toDataURL("image/jpeg", q);
      }
      setPreview(dataUrl);
    } finally {
      setCapturing(false);
    }
  };

  const confirm = () => {
    if (preview && onCapture) onCapture(preview);
  };

  const retake = () => setPreview(null);

  if (error) {
    return (
      <div className="border-2 border-red-500 bg-red-50 p-6" data-testid={`${testIdPrefix}-error`}>
        <div className="flex items-center gap-3 text-red-700">
          <AlertTriangle size={20} />
          <span className="font-mono text-sm">{error}</span>
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            data-testid={`${testIdPrefix}-cancel`}
            className="mt-4 h-11 px-5 border border-neutral-900 bg-white font-mono text-xs uppercase tracking-wider"
          >
            Cancel
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full" data-testid={`${testIdPrefix}-root`}>
      <div className="mono-label mb-3" data-testid={`${testIdPrefix}-prompt`}>{prompt.toUpperCase()}</div>
      <div className="relative w-full max-w-md mx-auto aspect-square bg-black border-2 border-black overflow-hidden">
        {!preview ? (
          <>
            <video
              ref={attachVideo}
              data-testid={`${testIdPrefix}-video`}
              playsInline
              muted
              autoPlay
              className="absolute inset-0 w-full h-full object-cover"
              style={{ transform: "scaleX(-1)" }}
            />
            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center text-neutral-400 font-mono text-xs">
                Initialising camera…
              </div>
            )}
            <Brackets />
          </>
        ) : (
          <img
            src={preview}
            alt="capture preview"
            data-testid={`${testIdPrefix}-preview`}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />
      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        {!preview ? (
          <>
            <button
              type="button"
              onClick={capture}
              disabled={!ready || capturing}
              data-testid={`${testIdPrefix}-capture-btn`}
              className="h-14 px-8 bg-black text-white font-mono text-sm uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-40"
            >
              <Camera size={16} /> {capturing ? "Capturing…" : actionLabel}
            </button>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                data-testid={`${testIdPrefix}-cancel`}
                className="h-14 px-6 border border-neutral-900 bg-white font-mono text-sm uppercase tracking-wider"
              >
                Cancel
              </button>
            )}
          </>
        ) : (
          <>
            {showRetake && (
              <button
                type="button"
                onClick={retake}
                data-testid={`${testIdPrefix}-retake-btn`}
                className="h-14 px-6 border border-neutral-900 bg-white font-mono text-sm uppercase tracking-wider inline-flex items-center gap-2"
              >
                <RotateCcw size={14} /> Retake
              </button>
            )}
            <button
              type="button"
              onClick={confirm}
              data-testid={`${testIdPrefix}-confirm-btn`}
              className="h-14 px-8 bg-black text-white font-mono text-sm uppercase tracking-wider"
            >
              Use this photo
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Brackets() {
  const common = "absolute w-6 h-6 border-[#22C55E]";
  return (
    <>
      <span className={`${common} top-2 left-2 border-l-2 border-t-2`} />
      <span className={`${common} top-2 right-2 border-r-2 border-t-2`} />
      <span className={`${common} bottom-2 left-2 border-l-2 border-b-2`} />
      <span className={`${common} bottom-2 right-2 border-r-2 border-b-2`} />
    </>
  );
}
